/* Reduce-motion accessibility preference.
 *
 * Tri-state, explicit-wins-over-OS:
 *   - user.reduceMotion === true  → always reduce
 *   - user.reduceMotion === false → always animate (overrides the OS setting)
 *   - user.reduceMotion == null   → follow the OS `prefers-reduced-motion`
 *     (Windows "Show animations" / macOS "Reduce motion") query.
 *
 * So a user who explicitly turns the toggle off gets animation back even if their
 * OS requests reduced motion, while someone who never opens Settings still has
 * their system preference respected.
 *
 * - useReduceMotion() returns the effective boolean for JS-driven motion
 *   (recharts `isAnimationActive`, the canvas supply-map loop).
 * - useApplyReduceMotion() reflects it onto the document root as
 *   `data-reduce-motion`, which global CSS uses to neutralize transitions and
 *   keyframe animations. Call it once near the app shell. */
import { useEffect, useState } from 'react';
import { useAuth } from './queries';

const MQ = '(prefers-reduced-motion: reduce)';

function useOsReduceMotion(): boolean {
  const [os, setOs] = useState<boolean>(() =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(MQ).matches
      : false,
  );
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia(MQ);
    const onChange = () => setOs(mq.matches);
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, []);
  return os;
}

export function useReduceMotion(): boolean {
  const { user } = useAuth();
  const os = useOsReduceMotion();
  // Explicit preference (true/false) wins; null/undefined falls back to the OS.
  return user?.reduceMotion ?? os;
}

export function useApplyReduceMotion(): void {
  const reduce = useReduceMotion();
  useEffect(() => {
    const root = document.documentElement;
    if (reduce) root.setAttribute('data-reduce-motion', 'true');
    else root.removeAttribute('data-reduce-motion');
  }, [reduce]);
}
